import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/* --------------------------------- Generate UTR Link ------------------------------ */

interface generateUtrLinkRequest {
  tenantId: number;
  vpaId: number;
}

interface generateUtrLinkSuccessResponse {
  utr: string;
  consentedDate: string;
  tenantVpaId: number;
  name: string;
  updatedAt: string;
  createdAt: string;
}

export const generateUtrLink = createAsyncThunk<{ link: string }, generateUtrLinkRequest>("utr/generateUtrLink", async (payload, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");

  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/utr/link`,
      timeout: 10000,
      data: payload,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return {
      link: response.data,
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Something went wrong");
  }
});

/* --------------------------------- Create ------------------------------ */

interface createUtrRequest {
  utr: string;
  tenantId: number;
  vpaId: number;
  transactionDate: Date;
}

interface createUtrSuccessResponse {
  utr: string;
  consentedDate: string;
  tenantVpaId: number;
  name: string;
  transactionDate: string;
  updatedAt: string;
  createdAt: string;
}

export const createUtr = createAsyncThunk<createUtrSuccessResponse, createUtrRequest>("utr/createUtr", async (payload, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken");

  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/utr`,
      timeout: 10000,
      data: payload,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Something went wrong");
  }
});

/* --------------------------------- Main State ------------------------------ */

interface UtrState {
  utrLoading: boolean;
  utrError: any;
  utr: null | createUtrSuccessResponse | generateUtrLinkSuccessResponse;
  link: null | string;
}

const initialState: UtrState = {
  utrLoading: false,
  utrError: null,
  utr: null,
  link: null,
};

const UtrSlice = createSlice({
  name: "utr",
  initialState,
  reducers: {
    clearUtr(state) {
      state.utr = null;
      state.utrLoading = false;
      state.utrError = null;
      state.link = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // Generate UTR Link
      .addCase(generateUtrLink.pending, (state) => {
        state.utrLoading = true;
        state.utrError = null;
      })
      .addCase(generateUtrLink.fulfilled, (state, action) => {
        state.utrLoading = false;
        state.link = action.payload.link;
      })
      .addCase(generateUtrLink.rejected, (state, action) => {
        state.utrLoading = false;
        state.utrError = action.payload;
      })

      // Create UTR
      .addCase(createUtr.pending, (state) => {
        state.utrLoading = true;
        state.utrError = null;
      })
      .addCase(createUtr.fulfilled, (state, action: PayloadAction<createUtrSuccessResponse>) => {
        state.utrLoading = false;
        state.utr = action.payload;
      })
      .addCase(createUtr.rejected, (state, action) => {
        state.utrLoading = false;
        state.utrError = action.payload || "Failed to update tenant";
      });
  },
});

export const { clearUtr } = UtrSlice.actions;

export default UtrSlice.reducer;
